import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney, formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  AD_NETWORKS,
  adsReport,
  connectAdAccount,
  declareCampaignUtm,
  disconnectAdAccount,
  listAdAccounts,
  syncAdAccount,
  type AdAccount,
  type AdNetwork,
  type CampaignReportRow,
} from './api';

/**
 * Spend against what it bought (context.md §77).
 *
 * One table: each campaign with what the network says it cost and did, beside what the CRM says
 * it produced — leads that carry the campaign, deals won, revenue — and the three figures a
 * marketer is asked for on a Monday: cost per lead, cost per sale, return on spend. A figure the
 * data cannot support is shown as a dash, never as zero (A-034). The network's own lead count sits
 * next to the CRM's on purpose: the gap between them is the most useful number on the screen.
 */
export function AdsPage(): React.JSX.Element {
  const client = useQueryClient();
  const [range, setRange] = useState(() => lastDays(30));
  const [accountId, setAccountId] = useState('');
  const [accountsOpen, setAccountsOpen] = useState(false);

  const accounts = useQuery({ queryKey: ['ad-accounts'], queryFn: listAdAccounts });
  const report = useQuery({
    queryKey: ['ads-report', range.from, range.to, accountId],
    queryFn: () => adsReport({ ...range, ...(accountId ? { accountId } : {}) }),
    enabled: range.from <= range.to,
  });

  const connected = (accounts.data?.accounts ?? []).filter((a) => a.status !== 'DISABLED');
  const data = report.data;

  const invalidate = (): void => void client.invalidateQueries({ queryKey: ['ads-report'] });

  const columns: Column<CampaignReportRow>[] = [
    {
      key: 'name',
      header: t('ads.campaign'),
      text: (row) =>
        `${row.name} ${row.accountName} ${t(`ads.network.${row.network}`)} ${row.status}`,
      render: (row) => (
        <span className="cell-stack">
          <span className="strong">{row.name}</span>
          <span className="muted small">
            {row.accountName} · {t(`ads.network.${row.network}`)}
            {row.status !== 'ACTIVE' && ` · ${row.status.toLowerCase()}`}
          </span>
        </span>
      ),
    },
    {
      key: 'spend',
      header: t('ads.spend'),
      align: 'end',
      text: (row) => formatMoney(row.spendCents),
      render: (row) => formatMoney(row.spendCents),
    },
    {
      key: 'clicks',
      header: t('ads.clicks'),
      align: 'end',
      text: (row) => formatNumber(row.clicks),
      render: (row) => formatNumber(row.clicks),
    },
    {
      key: 'leads',
      header: t('ads.leads'),
      align: 'end',
      text: (row) =>
        row.networkLeads !== null
          ? `${formatNumber(row.crmLeads)} / ${formatNumber(row.networkLeads)}`
          : formatNumber(row.crmLeads),
      render: (row) => (
        <>
          {formatNumber(row.crmLeads)}
          {row.networkLeads !== null && (
            <span className="muted small">{` / ${formatNumber(row.networkLeads)}`}</span>
          )}
        </>
      ),
    },
    {
      key: 'cpl',
      header: t('ads.cpl'),
      align: 'end',
      text: (row) => money(row.costPerLeadCents),
      render: (row) => money(row.costPerLeadCents),
    },
    {
      key: 'won',
      header: t('ads.won'),
      align: 'end',
      text: (row) => formatNumber(row.won),
      render: (row) => formatNumber(row.won),
    },
    {
      key: 'revenue',
      header: t('ads.revenue'),
      align: 'end',
      text: (row) => formatMoney(row.revenueCents),
      render: (row) => formatMoney(row.revenueCents),
    },
    {
      key: 'roas',
      header: t('ads.roas'),
      align: 'end',
      text: (row) => ratio(row.returnOnSpend),
      render: (row) => ratio(row.returnOnSpend),
    },
    {
      key: 'utmCampaign',
      header: t('ads.utmCampaign'),
      text: (row) => row.utmCampaign ?? row.externalId,
      render: (row) => <UtmCell row={row} onChanged={invalidate} />,
    },
    /*
     * Off by default.
     *
     * The nine columns above are the Monday questions; these four are the ones asked when a figure
     * looks wrong — what the network was told to spend, how much of it was seen, and whether the
     * campaign was even running.
     */
    {
      key: 'impressions',
      header: t('ads.impressions'),
      hidden: true,
      align: 'end',
      text: (row) => formatNumber(row.impressions),
      render: (row) => formatNumber(row.impressions),
    },
    {
      key: 'cpa',
      header: t('ads.cpa'),
      hidden: true,
      align: 'end',
      text: (row) => money(row.costPerWonCents),
      render: (row) => money(row.costPerWonCents),
    },
    {
      key: 'dailyBudget',
      header: t('ads.dailyBudget'),
      hidden: true,
      align: 'end',
      text: (row) => money(row.dailyBudgetCents),
      render: (row) => money(row.dailyBudgetCents),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      hidden: true,
      text: (row) => row.status.toLowerCase(),
      render: (row) => <span className="muted">{row.status.toLowerCase()}</span>,
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('ads.title')}
        description={t('ads.description')}
        actions={
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setAccountsOpen(true)}
          >
            {t('ads.accounts')}
            {connected.length > 0 && <span className="small">{` · ${connected.length}`}</span>}
          </button>
        }
      />

      {accounts.data && connected.length === 0 && (
        <p className="alert alert--info">
          {t('ads.noAccounts')}{' '}
          <button type="button" className="link" onClick={() => setAccountsOpen(true)}>
            {t('ads.connectFirst')}
          </button>
        </p>
      )}

      <div className="toolbar">
        <span className="cell-inline">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              type="button"
              className={`chip${range.from === lastDays(days).from ? ' chip--active' : ''}`}
              onClick={() => setRange(lastDays(days))}
            >
              {t('ads.lastDays').replace('{n}', String(days))}
            </button>
          ))}
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
          />
          <span className="muted small">{t('ads.to')}</span>
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
          />
          {connected.length > 1 && (
            <select
              className="select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">{t('ads.allAccounts')}</option>
              {connected.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          )}
        </span>
      </div>

      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : data ? (
        <>
          <div className="metrics">
            <Metric label={t('ads.spend')} value={formatMoney(data.totals.spendCents)} />
            <Metric
              label={t('ads.crmLeads')}
              value={formatNumber(data.totals.crmLeads)}
              hint={
                data.totals.networkLeads !== null
                  ? `${t('ads.networkSays')} ${formatNumber(data.totals.networkLeads)}`
                  : undefined
              }
            />
            <Metric label={t('ads.cpl')} value={money(data.totals.costPerLeadCents)} />
            <Metric
              label={t('ads.won')}
              value={formatNumber(data.totals.won)}
              hint={formatMoney(data.totals.revenueCents)}
            />
            <Metric label={t('ads.cpa')} value={money(data.totals.costPerWonCents)} />
            <Metric label={t('ads.roas')} value={ratio(data.totals.returnOnSpend)} />
          </div>

          {data.series.length > 1 && (
            <Panel title={t('ads.spendByDay')}>
              <Sparkline series={data.series} />
            </Panel>
          )}

          <Panel title={t('ads.campaigns')}>
            <DataTable
              tableKey="ads-campaigns"
              rows={data.campaigns}
              columns={columns}
              rowKey={(row) => row.id}
              isLoading={report.isLoading}
              error={report.error}
              onRetry={() => void report.refetch()}
              empty={{
                title: connected.length === 0 ? t('ads.noneYet') : t('ads.noneInRange'),
              }}
            />
          </Panel>
        </>
      ) : null}

      <AccountsDialog
        open={accountsOpen}
        accounts={accounts.data?.accounts ?? []}
        onClose={() => setAccountsOpen(false)}
        onChanged={() => {
          void client.invalidateQueries({ queryKey: ['ad-accounts'] });
          void client.invalidateQueries({ queryKey: ['ads-report'] });
        }}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      <strong className="metric__value">{value}</strong>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}

/**
 * The one editable cell in the report.
 *
 * A component rather than a closure because the edit box is per-row state, and because the CRM
 * side of every figure in the row depends on this string matching the utm_campaign the ads carry.
 */
function UtmCell({
  row,
  onChanged,
}: {
  row: CampaignReportRow;
  onChanged: () => void;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.utmCampaign ?? '');

  const save = useMutation({
    mutationFn: () => declareCampaignUtm(row.id, value.trim() || null),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  return editing ? (
    <span className="cell-inline">
      <input
        value={value}
        placeholder={row.externalId}
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        type="button"
        className="button button--compact"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {t('common.save')}
      </button>
    </span>
  ) : (
    <button type="button" className="link small" onClick={() => setEditing(true)}>
      {row.utmCampaign ?? t('ads.declareUtm')}
    </button>
  );
}

/** Spend per day as bars. Enough to see a spike; a chart library for one row of bars is not. */
function Sparkline({
  series,
}: {
  series: { day: string; spendCents: number }[];
}): React.JSX.Element {
  const max = Math.max(...series.map((point) => point.spendCents), 1);

  return (
    <div className="spend-bars" role="img" aria-label={t('ads.spendByDay')}>
      {series.map((point) => (
        <div
          key={point.day}
          className="spend-bars__bar"
          style={{ height: `${Math.max(2, (point.spendCents / max) * 100)}%` }}
          title={`${formatDate(point.day)} · ${formatMoney(point.spendCents)}`}
        />
      ))}
    </div>
  );
}

function AccountsDialog({
  open,
  accounts,
  onClose,
  onChanged,
}: {
  open: boolean;
  accounts: AdAccount[];
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const [adding, setAdding] = useState(false);
  const [network, setNetwork] = useState<AdNetwork>('LOCAL');
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [token, setToken] = useState('');
  const [developerToken, setDeveloperToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      setAdding(false);
      setError(null);
      element.showModal();
    }
    if (!open && element.open) element.close();
  }, [open]);

  const connect = useMutation({
    mutationFn: () =>
      connectAdAccount({
        network,
        name: name.trim(),
        externalId: externalId.trim() || null,
        config: {
          ...(token.trim() ? { accessToken: token.trim() } : {}),
          ...(developerToken.trim() ? { developerToken: developerToken.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setAdding(false);
      setName('');
      setExternalId('');
      setToken('');
      setDeveloperToken('');
      setError(null);
      onChanged();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const sync = useMutation({
    mutationFn: (id: string) => syncAdAccount(id),
    onSuccess: onChanged,
    onError: (c: unknown) => setError(messageFor(c)),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => disconnectAdAccount(id),
    onSuccess: onChanged,
    onError: (c: unknown) => setError(messageFor(c)),
  });

  if (!open) return null;

  const live = accounts.filter((account) => account.status !== 'DISABLED');
  const needsToken = network !== 'LOCAL';
  const canConnect =
    name.trim() !== '' &&
    (!needsToken || (token.trim() !== '' && externalId.trim() !== '')) &&
    (network !== 'GOOGLE' || developerToken.trim() !== '');

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form method="dialog" onSubmit={(event) => event.preventDefault()}>
        <h2>{t('ads.accounts')}</h2>
        <p className="muted">{t('ads.accountsHint')}</p>

        {live.length === 0 ? (
          <p className="muted">{t('ads.noAccountsYet')}</p>
        ) : (
          <ul className="social-accounts">
            {live.map((account) => (
              <li key={account.id} className="social-account">
                <span className="cell-stack">
                  <strong>{account.name}</strong>
                  <span className="muted small">
                    {t(`ads.network.${account.network}`)}
                    {account.externalId ? ` · ${account.externalId}` : ''}
                    {account.lastSyncedAt
                      ? ` · ${t('ads.synced')} ${formatRelative(account.lastSyncedAt)}`
                      : ` · ${t('ads.neverSynced')}`}
                  </span>
                  {account.syncError && (
                    <span className="small" style={{ color: 'var(--color-down)' }}>
                      {account.syncError}
                    </span>
                  )}
                </span>
                <Badge tone={account.status === 'CONNECTED' ? 'positive' : 'danger'}>
                  {t(`ads.accountStatus.${account.status}`)}
                </Badge>
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={sync.isPending}
                  onClick={() => sync.mutate(account.id)}
                >
                  {t('ads.syncNow')}
                </button>
                <button
                  type="button"
                  className="button button--quiet button--danger button--compact"
                  disabled={disconnect.isPending}
                  onClick={() => disconnect.mutate(account.id)}
                >
                  {t('social.disconnect')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {adding ? (
          <div className="social-connect">
            <div className="form-grid">
              <label className="stack__field">
                <span>{t('social.networkLabel')}</span>
                <select
                  className="select"
                  value={network}
                  onChange={(event) => setNetwork(event.target.value as AdNetwork)}
                >
                  {AD_NETWORKS.map((key) => (
                    <option key={key} value={key}>
                      {t(`ads.network.${key}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stack__field">
                <span>
                  {t('social.accountName')} <span className="required">*</span>
                </span>
                <input
                  value={name}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
            </div>

            {needsToken && (
              <div className="form-grid">
                <label className="stack__field">
                  <span>
                    {t(`ads.externalId.${network}`)} <span className="required">*</span>
                  </span>
                  <input
                    value={externalId}
                    maxLength={200}
                    onChange={(event) => setExternalId(event.target.value)}
                  />
                </label>
                <label className="stack__field">
                  <span>
                    {t('social.accessToken')} <span className="required">*</span>
                  </span>
                  <input
                    type="password"
                    value={token}
                    autoComplete="off"
                    onChange={(event) => setToken(event.target.value)}
                  />
                </label>
                {network === 'GOOGLE' && (
                  <label className="stack__field">
                    <span>
                      {t('ads.developerToken')} <span className="required">*</span>
                    </span>
                    <input
                      type="password"
                      value={developerToken}
                      autoComplete="off"
                      onChange={(event) => setDeveloperToken(event.target.value)}
                    />
                  </label>
                )}
              </div>
            )}

            {needsToken && <p className="muted small">{t('ads.tokenHint')}</p>}

            <div className="confirm__actions">
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setAdding(false)}
              >
                {t('confirm.cancel')}
              </button>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={!canConnect || connect.isPending}
                onClick={() => connect.mutate()}
              >
                {t('social.connect')}
              </button>
            </div>
          </div>
        ) : (
          <div className="confirm__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onClose}
            >
              {t('common.close')}
            </button>
            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => setAdding(true)}
            >
              {t('social.connectAccount')}
            </button>
          </div>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </form>
    </dialog>
  );
}

function money(cents: number | null): string {
  return cents === null ? '—' : formatMoney(cents);
}

function ratio(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2).replace('.', ',')}×`;
}

function lastDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86_400_000);
  const iso = (date: Date): string => date.toISOString().slice(0, 10);

  return { from: iso(from), to: iso(to) };
}
